import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import HireMoneyActions from './HireMoneyActions';
import { AdminHireAPI } from '../../services/api';
import {
  PAYMENT_STATE_LABELS, NEEDS_ACTION_LABELS, formatRand, formatRange, formatDateTime, hireStatusLabel, timelineText, apiErrorMessage,
} from './charterUtils';

const Card = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
    <h2 className="text-sm font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 text-right">{children}</span>
  </div>
);

/**
 * One hire for admin (spec 6.4): status, the trip, the people, the ledger
 * and the timeline, with Refund and Cancel for super admins. Money actions
 * send the version this screen loaded, so a stale screen is refused (409)
 * and reloaded rather than acting twice.
 */
const HireDetail = ({ hireId, isSuperAdmin, onBack }) => {
  const [hire, setHire] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await AdminHireAPI.get(hireId);
      setHire(res.data.data.hire);
      setError('');
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load this hire'));
    }
  }, [hireId]);

  useEffect(() => { load(); }, [load]);

  const back = (
    <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-4 text-sm font-medium">
      <ArrowLeft size={16} className="mr-1" /> Back to Hires
    </button>
  );

  if (!hire) {
    return <div>{back}<p className={`text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>{error || 'Loading...'}</p></div>;
  }

  const ledger = hire.ledger || {};
  const stopsByDay = (hire.stops || []).reduce((acc, s) => {
    const day = s.day || 1;
    (acc[day] = acc[day] || []).push(s);
    return acc;
  }, {});

  return (
    <div>
      {back}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 font-mono">{hire.bookingCode}</h1>
            <p className="text-sm text-gray-600">{formatRange(hire.startAt, hire.endAt)} · {hire.partySize} passengers</p>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-gray-900">{hireStatusLabel(hire)}</div>
            <div className="text-sm text-gray-600">{PAYMENT_STATE_LABELS[hire.paymentState] || hire.paymentState}</div>
          </div>
        </div>
        {(hire.needsAction || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {hire.needsAction.map((r) => <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">{NEEDS_ACTION_LABELS[r] || r}</span>)}
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Trip">
          <Row label="Purpose">{(hire.purpose || '').replace(/_/g, ' ')}</Row>
          <Row label="Class">{hire.vehicleClass}{hire.upgraded ? ' (upgraded)' : ''}</Row>
          {hire.responseDueAt && hire.status === 'requested' && <Row label="Reply due">{formatDateTime(hire.responseDueAt)}</Row>}
          {hire.payBy && hire.status === 'awaiting_payment' && <Row label="Pay by">{formatDateTime(hire.payBy)}</Row>}
          {hire.schedule && hire.schedule.balanceAt && hire.status === 'confirmed' && hire.paymentState === 'deposit_paid' && (
            <Row label="Balance charged at">{formatDateTime(hire.schedule.balanceAt)}</Row>
          )}
          <div className="mt-3 space-y-2">
            {Object.keys(stopsByDay).map((day) => (
              <div key={day}>
                <div className="text-xs font-medium text-gray-500">Day {day}</div>
                <ol className="list-decimal list-inside text-sm text-gray-800">
                  {stopsByDay[day].map((s, i) => <li key={`${day}-${i}`}>{s.name || s.address}</li>)}
                </ol>
              </div>
            ))}
          </div>
          {hire.notes && <p className="mt-3 text-sm text-gray-700">Notes: {hire.notes}</p>}
        </Card>

        <Card title="People and vehicle">
          <Row label="Operator">{hire.operator && hire.operator.companyName}</Row>
          <Row label="Operator contact">{hire.operator && [hire.operator.phoneNumber, hire.operator.email].filter(Boolean).join(' · ')}</Row>
          <Row label="Traveller">{hire.traveler && hire.traveler.name}</Row>
          <Row label="Traveller contact">{hire.traveler && [hire.traveler.phone, hire.traveler.email].filter(Boolean).join(' · ')}</Row>
          <Row label="Vehicle">{hire.vehicle ? `${[hire.vehicle.vehicleMake, hire.vehicle.model].filter(Boolean).join(' ')} ${hire.vehicle.licensePlate || ''} (${hire.vehicle.capacity} seats)` : 'None'}</Row>
          <Row label="Driver">{hire.driver ? `${hire.driver.name}${hire.driver.phoneNumber ? ` · ${hire.driver.phoneNumber}` : ''}` : 'Not assigned'}</Row>
          {hire.cardLast4 && <Row label="Card">ending {hire.cardLast4}</Row>}
        </Card>

        <Card title="Payments">
          <Row label="Price">{formatRand(hire.totalCents)}</Row>
          <Row label="Charged">{formatRand(ledger.chargedCents)}</Row>
          <Row label="Refunded">{formatRand(ledger.refundedCents)}</Row>
          <Row label="Taken">{formatRand(ledger.netCents)}</Row>
          <Row label="Zuttle">{formatRand(ledger.commissionCents)}</Row>
          <Row label="Operator">{formatRand(ledger.operatorCents)}</Row>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="ledger">
              <thead><tr className="text-left text-gray-500">{['Reference', 'Kind', 'Amount', 'Status', 'By', 'When'].map((h) => <th key={h} className="py-1 pr-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {(hire.payments || []).map((p) => (
                  <tr key={p.reference} className="border-t border-gray-100">
                    <td className="py-1 pr-2 font-mono">{p.reference}</td>
                    <td className="py-1 pr-2">{p.kind}</td>
                    <td className="py-1 pr-2 whitespace-nowrap">{formatRand(p.amountCents)}</td>
                    <td className="py-1 pr-2">{p.status}{p.failureReason ? ` (${p.failureReason})` : ''}</td>
                    <td className="py-1 pr-2">{p.initiatedBy}</td>
                    <td className="py-1 pr-2 whitespace-nowrap">{formatDateTime(p.settledAt || p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hire.cancellation && (
            <p className="mt-3 text-sm text-gray-700">
              Cancelled {formatDateTime(hire.cancellation.at)}: kept {formatRand(hire.cancellation.keepCents)}, refunded {formatRand(hire.cancellation.refundCents)}
              {hire.cancellation.s17_5 ? ` · s17(5) ${hire.cancellation.s17_5}` : ''}
            </p>
          )}
        </Card>

        <Card title="Timeline">
          <ol className="space-y-2" data-testid="timeline">
            {(hire.timeline || []).map((e, i) => (
              <li key={`${e.event}-${i}`} className="text-sm">
                <span className="text-xs text-gray-500 mr-2">{formatDateTime(e.at)}</span>
                <span className="text-gray-900">{timelineText(e)}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <div className="mt-6">
        {isSuperAdmin
          ? <HireMoneyActions hire={hire} onChanged={load} />
          : <p className="text-sm text-gray-500">Refund and cancel need a super admin.</p>}
      </div>
    </div>
  );
};

export default HireDetail;
